import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode
} from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router'
import { create } from 'zustand'
import { chevronBack, chevronForward, menu as menuIcon } from 'ionicons/icons'
import { Icon } from './Icon'
import { usePublishHeaderHeight } from './layout'
import { useModalDialog } from './dialog'
import { resolveLocation, type RouteLocation } from '../router/navigation'

/* ---------------------------------------------------------------------------
   Shared types
   ------------------------------------------------------------------------ */

export type Color =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'medium'
  | 'light'
  | 'dark'

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ')

/**
 * True for an unmodified primary click — the link will navigate in this tab.
 * Use it to gate side effects on links (closing a modal or menu) so
 * Cmd/Ctrl/Shift-click "open in new tab" leaves the current view alone.
 */
export const isPlainClick = (e: React.MouseEvent) =>
  e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)

interface BaseProps {
  className?: string
  style?: CSSProperties
  children?: ReactNode
  id?: string
}

/* ---------------------------------------------------------------------------
   Menu state (ion-menu / ion-menu-button / menuController)
   ------------------------------------------------------------------------ */

interface MenuState {
  open: boolean
  /** True while the app shell has a side menu mounted (not on embeds or the error page). */
  available: boolean
  /**
   * Something in the menu wants attention (payments due). Shown on the menu
   * button, since the drawer hides the in-menu badge below desktop width.
   * `label` completes "Open menu, <count> <label>" for screen readers.
   */
  badge: { count: number; label: string } | null
  setOpen: (open: boolean) => void
  toggle: () => void
}

export const useMenu = create<MenuState>((set) => ({
  open: false,
  available: false,
  badge: null,
  setOpen: (open) => set({ open }),
  toggle: () => set((s) => ({ open: !s.open }))
}))

export const menuController = {
  open: async () => useMenu.getState().setOpen(true),
  close: async () => useMenu.getState().setOpen(false),
  toggle: async () => useMenu.getState().toggle()
}

/* ---------------------------------------------------------------------------
   Page structure (ion-page / ion-header / ion-toolbar / ion-content)
   ------------------------------------------------------------------------ */

export function Page ({ className, style, children, id }: BaseProps) {
  return (
    <div className={cx('page', className)} style={style} id={id}>
      {children}
    </div>
  )
}

export function Header ({ className, style, children }: BaseProps) {
  // On a routed page the header is sticky; its height feeds --page-header-height.
  const ref = useRef<HTMLElement>(null)
  usePublishHeaderHeight(ref)
  return (
    <header ref={ref} className={cx('app-header', className)} style={style}>
      {children}
    </header>
  )
}

export function Toolbar ({ className, style, children }: BaseProps) {
  return (
    <div className={cx('toolbar', className)} style={style}>
      {children}
    </div>
  )
}

export function Title ({ className, style, children }: BaseProps) {
  return (
    <h1 className={cx('toolbar-title', className)} style={style}>
      {children}
    </h1>
  )
}

export function Buttons ({ slot = 'start', className, style, children }: BaseProps & { slot?: 'start' | 'end' | 'primary' | 'secondary' }) {
  const side = slot === 'end' || slot === 'primary' ? 'end' : 'start'
  return (
    <div className={cx('toolbar-buttons', `toolbar-buttons--${side}`, className)} style={style}>
      {children}
    </div>
  )
}

/** ion-back-button: history back, or `defaultHref` when there is no history. */
export function BackButton ({ defaultHref = '/', text, className }: { defaultHref?: string; text?: string; className?: string }) {
  const navigate = useNavigate()
  const onClick = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) navigate(-1)
    else navigate(defaultHref, { replace: true })
  }
  return (
    <button type="button" className={cx('toolbar-button', className)} onClick={onClick} aria-label={text || 'Back'}>
      <Icon icon={chevronBack} />
      {text ? <span>{text}</span> : null}
    </button>
  )
}

/**
 * Opens the side-menu drawer. Hidden by CSS at desktop widths, where the menu
 * is a persistent sidebar, and not rendered when no menu is mounted.
 */
export function MenuButton ({ className }: { className?: string }) {
  const toggle = useMenu((s) => s.toggle)
  const open = useMenu((s) => s.open)
  const available = useMenu((s) => s.available)
  const badge = useMenu((s) => s.badge)
  if (!available) return null
  const count = badge && badge.count > 0 ? badge.count : 0
  return (
    <button
      type="button"
      className={cx('toolbar-button', 'menu-button', className)}
      onClick={toggle}
      aria-label={count ? `Open menu, ${count} ${badge!.label}` : 'Open menu'}
      aria-expanded={open}
      aria-controls="app-menu"
    >
      <Icon icon={menuIcon} />
      {/* The count is already in the label, so the visual badge is hidden from AT. */}
      {count ? (
        <span className="badge badge--warning menu-button__badge" aria-hidden="true">
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </button>
  )
}

/**
 * Convenience header: menu or back button on the left, title, actions on the right.
 * Equivalent to the ion-header > ion-toolbar block most legacy pages repeat.
 *
 * Every page offers the main menu below desktop width: `menu` puts the button
 * first; otherwise (pushed detail pages with a back button) it trails the end
 * actions. `menu={false}` opts out.
 */
export function PageHeader ({
  title,
  back,
  menu,
  end,
  start,
  children
}: {
  title?: ReactNode
  /** Default href for the back button; when set a back button is shown. */
  back?: string
  /** true: menu button leads; undefined: it trails the end slot; false: none. */
  menu?: boolean
  start?: ReactNode
  end?: ReactNode
  /** Extra toolbars (e.g. a segment row) rendered under the main toolbar. */
  children?: ReactNode
}) {
  return (
    <Header>
      <Toolbar>
        <Buttons slot="start">
          {menu ? <MenuButton /> : null}
          {back !== undefined ? <BackButton defaultHref={back} /> : null}
          {start}
        </Buttons>
        <Title>{title}</Title>
        <Buttons slot="end">
          {end}
          {menu === undefined ? <MenuButton /> : null}
        </Buttons>
      </Toolbar>
      {children}
    </Header>
  )
}

export function Content ({
  className,
  style,
  children,
  padding,
  fullscreen: _fullscreen,
  scrollRef
}: BaseProps & {
  padding?: boolean
  fullscreen?: boolean
  /** The element itself. Only a scroller inside modals; routed pages scroll the document. */
  scrollRef?: React.Ref<HTMLDivElement>
}) {
  return (
    <main className={cx('content', padding && 'padded', className)} style={style} ref={scrollRef}>
      {children}
    </main>
  )
}

/* ---------------------------------------------------------------------------
   Buttons
   ------------------------------------------------------------------------ */

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  color?: Color
  fill?: 'solid' | 'outline' | 'clear' | 'default'
  expand?: 'block' | 'full'
  size?: 'small' | 'default' | 'large'
  /** In-app destination (ion-button router-link). Renders a real link so middle-click / open-in-new-tab work. */
  routerLink?: RouteLocation
  /** Replace the current history entry instead of pushing (with `routerLink`). */
  replace?: boolean
  /** Open an external URL. */
  href?: string
  target?: string
  /** Render a spinner and disable while true. */
  loading?: boolean
  iconOnly?: boolean
  slot?: string
}

export function Button ({
  color = 'primary',
  fill = 'solid',
  expand,
  size,
  routerLink,
  replace,
  href,
  target,
  loading,
  iconOnly,
  className,
  children,
  onClick,
  disabled,
  type = 'button',
  slot: _slot,
  ...rest
}: ButtonProps) {
  const classes = cx(
    'btn',
    `btn--${color}`,
    fill === 'outline' && 'btn--outline',
    fill === 'clear' && 'btn--clear',
    expand && `btn--${expand}`,
    size && size !== 'default' && `btn--${size}`,
    iconOnly && 'btn--icon-only',
    className
  )
  // Links only while actionable; a disabled/loading link falls through to a disabled <button>.
  const linkProps = rest as AnchorHTMLAttributes<HTMLAnchorElement>
  const linkClick = onClick as unknown as MouseEventHandler<HTMLAnchorElement> | undefined
  if (routerLink !== undefined && !disabled && !loading) {
    return (
      <Link {...linkProps} to={resolveLocation(routerLink)} replace={replace} className={classes} onClick={linkClick}>
        {children}
      </Link>
    )
  }
  if (href && !disabled) {
    return (
      <a {...linkProps} className={classes} href={href} target={target} rel={target === '_blank' ? 'noopener noreferrer' : undefined} onClick={linkClick}>
        {children}
      </a>
    )
  }
  return (
    <button
      {...rest}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
    >
      {loading ? <span className="spinner" aria-hidden /> : null}
      {children}
    </button>
  )
}

/* ---------------------------------------------------------------------------
   Cards
   ------------------------------------------------------------------------ */

export function Card ({ className, style, children, onClick, button, id }: BaseProps & { onClick?: () => void; button?: boolean }) {
  const interactive = Boolean(onClick) || button
  return (
    <section
      id={id}
      className={cx('card', interactive && 'card--button', className)}
      style={style}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.() } } : undefined}
    >
      {children}
    </section>
  )
}

export function CardHeader ({ className, style, children }: BaseProps) {
  return <div className={cx('card-header', className)} style={style}>{children}</div>
}

export function CardTitle ({ className, style, children }: BaseProps) {
  return <h2 className={cx('card-title', className)} style={style}>{children}</h2>
}

export function CardSubtitle ({ className, style, children }: BaseProps) {
  return <p className={cx('card-subtitle', className)} style={style}>{children}</p>
}

export function CardContent ({ className, style, children }: BaseProps) {
  return <div className={cx('card-content', className)} style={style}>{children}</div>
}

/* ---------------------------------------------------------------------------
   Lists and items
   ------------------------------------------------------------------------ */

export function List ({ className, style, children, inset }: BaseProps & { inset?: boolean; lines?: string }) {
  return <div className={cx('list', inset && 'list--inset', className)} style={style} role="list">{children}</div>
}

export function ListHeader ({ className, style, children }: BaseProps) {
  return <div className={cx('list-header', className)} style={style}>{children}</div>
}

export interface ItemProps extends BaseProps {
  /** Content before the label (ion-item slot="start"). */
  start?: ReactNode
  /** Content after the label (ion-item slot="end"). Keep it non-interactive on link rows. */
  end?: ReactNode
  button?: boolean
  onClick?: (e: React.MouseEvent) => void
  /** In-app destination; the row renders as a link. */
  routerLink?: RouteLocation
  /** Replace the current history entry instead of pushing (with `routerLink`). */
  replace?: boolean
  /** External URL (opens in a new tab). */
  href?: string
  detail?: boolean
  lines?: 'full' | 'inset' | 'none'
  disabled?: boolean
  color?: Color
  /** Passed through to the row element (e.g. aria-current). */
  ariaCurrent?: React.AriaAttributes['aria-current']
  ariaLabel?: string
}

export function Item ({ start, end, button, onClick, routerLink, replace, href, detail, lines, disabled, className, style, children, id, ariaCurrent, ariaLabel }: ItemProps) {
  const isLink = routerLink !== undefined || Boolean(href)
  const interactive = button || Boolean(onClick) || isLink
  const showDetail = detail ?? (interactive && routerLink !== undefined)
  const classes = cx(
    'item',
    interactive && 'item--button',
    lines && `item--lines-${lines}`,
    disabled && 'item--disabled',
    className
  )
  const inner = (
    <>
      {start ? <span className="item__start">{start}</span> : null}
      <span className="item__inner">
        {children}
        {end ? <span className="item__end">{end}</span> : null}
        {showDetail ? <Icon icon={chevronForward} className="item__detail" /> : null}
      </span>
    </>
  )
  if (href && !disabled) {
    return (
      <a id={id} className={classes} style={style} href={href} target="_blank" rel="noopener noreferrer" aria-current={ariaCurrent} aria-label={ariaLabel} onClick={onClick}>
        {inner}
      </a>
    )
  }
  if (routerLink !== undefined && !disabled) {
    return (
      <Link id={id} className={classes} style={style} to={resolveLocation(routerLink)} replace={replace} aria-current={ariaCurrent} aria-label={ariaLabel} onClick={onClick}>
        {inner}
      </Link>
    )
  }
  if (interactive && !isLink) {
    return (
      <div
        id={id}
        className={classes}
        style={style}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-current={ariaCurrent}
        aria-label={ariaLabel}
        onClick={(e) => {
          if (disabled) return
          onClick?.(e)
        }}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.target !== e.currentTarget) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement).click()
          }
        }}
      >
        {inner}
      </div>
    )
  }
  // Static rows, and disabled links (no href, so nothing to follow).
  return <div id={id} className={classes} style={style} role="listitem" aria-disabled={disabled || undefined} aria-current={ariaCurrent} aria-label={ariaLabel}>{inner}</div>
}

export function Label ({ className, style, children, wrap }: BaseProps & { wrap?: boolean; position?: string }) {
  return <div className={cx('label', wrap !== false && 'label--wrap', className)} style={style}>{children}</div>
}

export function Note ({ className, style, children, color }: BaseProps & { color?: Color }) {
  return <span className={cx('note', color && `text--${color}`, className)} style={style}>{children}</span>
}

export function Text ({ className, style, children, color }: BaseProps & { color?: Color }) {
  return <span className={cx(color && `text--${color}`, className)} style={style}>{children}</span>
}

/* ---------------------------------------------------------------------------
   Form controls
   ------------------------------------------------------------------------ */

interface FieldShell {
  label?: ReactNode
  labelPlacement?: 'stacked' | 'floating' | 'fixed' | 'start' | 'end'
  helperText?: ReactNode
  errorText?: ReactNode
  fill?: 'outline' | 'solid'
  className?: string
  style?: CSSProperties
}

function FieldWrap ({
  id,
  label,
  labelPlacement,
  helperText,
  errorText,
  fill,
  className,
  style,
  children
}: FieldShell & { id: string; children: ReactNode }) {
  const inline = labelPlacement === 'fixed' || labelPlacement === 'start'
  return (
    <div className={cx('field', inline && 'field--inline', fill === 'outline' && 'field--outline', Boolean(errorText) && 'field--invalid', className)} style={style}>
      {label ? <label className="field__label" htmlFor={id}>{label}</label> : null}
      {children}
      {errorText ? <span className="field__error" role="alert">{errorText}</span> : helperText ? <span className="field__helper">{helperText}</span> : null}
    </div>
  )
}

export interface InputProps extends FieldShell {
  value?: string | number | null
  /** Called with the raw string value on every keystroke (ion-input `ionInput`). */
  onChange?: (value: string) => void
  onBlur?: () => void
  onEnter?: () => void
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local' | 'month'
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  min?: number | string
  max?: number | string
  step?: number | string
  maxlength?: number
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  autocomplete?: string
  autoFocus?: boolean
  name?: string
  id?: string
}

export function Input ({
  value,
  onChange,
  onBlur,
  onEnter,
  type = 'text',
  placeholder,
  disabled,
  readonly,
  required,
  min,
  max,
  step,
  maxlength,
  inputMode,
  autocomplete,
  autoFocus,
  name,
  id,
  ...shell
}: InputProps) {
  const autoId = useId()
  const fieldId = id || autoId
  return (
    <FieldWrap id={fieldId} {...shell}>
      <input
        id={fieldId}
        className="field__control"
        type={type}
        name={name}
        value={value ?? ''}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        required={required}
        min={min}
        max={max}
        step={step}
        maxLength={maxlength}
        inputMode={inputMode}
        autoComplete={autocomplete}
        autoFocus={autoFocus}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onEnter ? (e) => { if (e.key === 'Enter') onEnter() } : undefined}
      />
    </FieldWrap>
  )
}

export interface TextareaProps extends FieldShell {
  value?: string | null
  onChange?: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  rows?: number
  autoGrow?: boolean
  disabled?: boolean
  readonly?: boolean
  maxlength?: number
  id?: string
}

export function Textarea ({ value, onChange, onBlur, placeholder, rows = 4, autoGrow, disabled, readonly, maxlength, id, ...shell }: TextareaProps) {
  const autoId = useId()
  const fieldId = id || autoId
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (!autoGrow || !ref.current) return
    ref.current.style.height = 'auto'
    ref.current.style.height = `${ref.current.scrollHeight + 2}px`
  }, [autoGrow, value])
  return (
    <FieldWrap id={fieldId} {...shell}>
      <textarea
        ref={ref}
        id={fieldId}
        className="field__control"
        value={value ?? ''}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        readOnly={readonly}
        maxLength={maxlength}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
      />
    </FieldWrap>
  )
}

export interface SelectOptionProps {
  value: string | number | boolean | null
  disabled?: boolean
  children?: ReactNode
}

/** Declarative option; rendered by <Select>. */
export function SelectOption (_props: SelectOptionProps): null {
  return null
}

type SelectValue = string | number | boolean | null | undefined

export interface SelectProps extends FieldShell {
  value?: SelectValue | SelectValue[]
  /** Receives the option's original value (not a string), or an array when `multiple`. */
  onChange?: (value: any) => void
  placeholder?: string
  multiple?: boolean
  disabled?: boolean
  interface?: string
  id?: string
  /** Either <SelectOption> children or an `options` array. */
  options?: Array<{ value: SelectValue; label: ReactNode; disabled?: boolean }>
  children?: ReactNode
}

function collectOptions (children: ReactNode): Array<{ value: SelectValue; label: ReactNode; disabled?: boolean }> {
  const out: Array<{ value: SelectValue; label: ReactNode; disabled?: boolean }> = []
  const walk = (node: ReactNode) => {
    if (Array.isArray(node)) { node.forEach(walk); return }
    if (!node || typeof node !== 'object' || !('props' in node)) return
    const el = node as React.ReactElement<any>
    if (el.type === SelectOption) {
      out.push({ value: el.props.value, label: el.props.children, disabled: el.props.disabled })
    } else if (el.props?.children) {
      walk(el.props.children)
    }
  }
  walk(children)
  return out
}

const optionKey = (v: SelectValue) => (v === null || v === undefined ? '' : `${typeof v}:${String(v)}`)

const labelText = (label: ReactNode): string => {
  if (label === null || label === undefined || typeof label === 'boolean') return ''
  if (typeof label === 'string' || typeof label === 'number') return String(label)
  if (Array.isArray(label)) return label.map(labelText).join('')
  if (typeof label === 'object' && 'props' in label) return labelText((label as React.ReactElement<any>).props.children)
  return ''
}

export function Select ({ value, onChange, placeholder, multiple, disabled, id, options, children, interface: _iface, ...shell }: SelectProps) {
  const autoId = useId()
  const fieldId = id || autoId
  const opts = options ?? collectOptions(children)
  const byKey = new Map(opts.map((o) => [optionKey(o.value), o.value]))
  const current = multiple
    ? (Array.isArray(value) ? value : []).map(optionKey)
    : optionKey(value as SelectValue)
  const hasValue = multiple ? (current as string[]).length > 0 : byKey.has(current as string)
  return (
    <FieldWrap id={fieldId} {...shell}>
      <select
        id={fieldId}
        className="field__control"
        multiple={multiple}
        disabled={disabled}
        value={multiple ? (current as string[]) : hasValue ? (current as string) : ''}
        onChange={(e) => {
          if (multiple) {
            onChange?.(Array.from(e.target.selectedOptions).map((o) => byKey.get(o.value)))
          } else {
            onChange?.(byKey.get(e.target.value))
          }
        }}
      >
        {!multiple && !hasValue ? <option value="" disabled>{placeholder || 'Select…'}</option> : null}
        {opts.map((o) => (
          <option key={optionKey(o.value)} value={optionKey(o.value)} disabled={o.disabled}>
            {labelText(o.label)}
          </option>
        ))}
      </select>
    </FieldWrap>
  )
}

export function Toggle ({
  checked,
  onChange,
  disabled,
  children,
  justify,
  className,
  ariaLabel
}: {
  checked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  children?: ReactNode
  /** 'space-between' puts the label first and the switch at the far end. */
  justify?: 'start' | 'space-between'
  labelPlacement?: string
  className?: string
  ariaLabel?: string
  slot?: string
}) {
  return (
    <label className={cx('toggle', justify === 'space-between' && 'toggle--end', className)}>
      <input
        type="checkbox"
        role="switch"
        checked={Boolean(checked)}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span className="toggle__track" aria-hidden />
      {children ? <span>{children}</span> : null}
    </label>
  )
}

export function Checkbox ({
  checked,
  onChange,
  disabled,
  children,
  className,
  indeterminate
}: {
  checked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  children?: ReactNode
  className?: string
  indeterminate?: boolean
  slot?: string
  labelPlacement?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate)
  }, [indeterminate])
  return (
    <label className={cx('check', className)}>
      <input ref={ref} type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} />
      {children ? <span>{children}</span> : null}
    </label>
  )
}

const RadioCtx = createContext<{ name: string; value: unknown; onChange?: (v: any) => void } | null>(null)

export function RadioGroup ({ value, onChange, children, className }: { value?: unknown; onChange?: (v: any) => void; children?: ReactNode; className?: string }) {
  const name = useId()
  return (
    <div className={cx('radio-group', className)} role="radiogroup">
      <RadioCtx.Provider value={{ name, value, onChange }}>{children}</RadioCtx.Provider>
    </div>
  )
}

export function Radio ({ value, children, disabled }: { value: unknown; children?: ReactNode; disabled?: boolean; slot?: string }) {
  const ctx = useContext(RadioCtx)
  return (
    <label className="check">
      <input
        type="radio"
        name={ctx?.name}
        checked={ctx ? ctx.value === value : false}
        disabled={disabled}
        onChange={() => ctx?.onChange?.(value)}
      />
      {children ? <span>{children}</span> : null}
    </label>
  )
}

export function Range ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled,
  pin,
  label,
  className,
  startSlot,
  endSlot,
  onCommit
}: {
  value?: number
  onChange?: (value: number) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  /** Show the current value beside the slider. */
  pin?: boolean
  label?: string
  className?: string
  snaps?: boolean
  ticks?: boolean
  /** Content before / after the slider (ion-range slot="start" / slot="end"). */
  startSlot?: ReactNode
  endSlot?: ReactNode
  /** Fires once when the user releases the slider (ion-range `ionChange`). */
  onCommit?: (value: number) => void
}) {
  return (
    <div className={cx('range', className)}>
      {startSlot}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value ?? min}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange?.(Number(e.target.value))}
        onPointerUp={onCommit ? (e) => onCommit(Number((e.target as HTMLInputElement).value)) : undefined}
        onKeyUp={onCommit ? (e) => onCommit(Number((e.target as HTMLInputElement).value)) : undefined}
      />
      {endSlot}
      {pin ? <span className="range__value">{value ?? min}</span> : null}
    </div>
  )
}

/* ---------------------------------------------------------------------------
   Segment
   ------------------------------------------------------------------------ */

const SegmentCtx = createContext<{ value: unknown; onChange?: (v: any) => void; disabled?: boolean } | null>(null)

export function Segment ({
  value,
  onChange,
  scrollable,
  disabled,
  className,
  style,
  children,
  ariaLabel,
  ariaLabelledby
}: BaseProps & { value?: unknown; onChange?: (v: any) => void; scrollable?: boolean; disabled?: boolean; ariaLabel?: string; ariaLabelledby?: string }) {
  return (
    <div className={cx('segment', scrollable && 'segment--scrollable', className)} style={style} role="tablist" aria-label={ariaLabel} aria-labelledby={ariaLabelledby}>
      <SegmentCtx.Provider value={{ value, onChange, disabled }}>{children}</SegmentCtx.Provider>
    </div>
  )
}

export function SegmentButton ({ value, disabled, className, children, ariaLabel, title }: BaseProps & { value: unknown; disabled?: boolean; ariaLabel?: string; title?: string }) {
  const ctx = useContext(SegmentCtx)
  const selected = ctx?.value === value
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      aria-label={ariaLabel}
      title={title}
      disabled={disabled || ctx?.disabled}
      className={cx('segment-button', className)}
      onClick={() => { if (!selected) ctx?.onChange?.(value) }}
    >
      {children}
    </button>
  )
}

/* ---------------------------------------------------------------------------
   Small display pieces
   ------------------------------------------------------------------------ */

export function Badge ({ color = 'primary', className, style, children }: BaseProps & { color?: Color; slot?: string }) {
  return <span className={cx('badge', `badge--${color}`, className)} style={style}>{children}</span>
}

export function Spinner ({ size, className, label = 'Loading' }: { size?: 'small'; className?: string; label?: string; name?: string; color?: string }) {
  return <span className={cx('spinner', size === 'small' && 'spinner--small', className)} role="status" aria-label={label} />
}

/* ---------------------------------------------------------------------------
   Skeleton: placeholder shapes for a page's first load
   ---------------------------------------------------------------------------
   Use these instead of a centred <Spinner> while a page's content loads for the
   first time, so the layout appears at once and doesn't jump when data lands.
   Keep <Spinner> for actions in progress (buttons, saves, refreshes).
   ------------------------------------------------------------------------ */

/** One grey placeholder bar or block. Decorative: wrap shapes in <SkeletonGroup>. */
export function Skeleton ({
  width,
  height,
  radius,
  className
}: { width?: string | number; height?: string | number; radius?: string; className?: string }) {
  return (
    <span
      className={cx('skeleton', className)}
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  )
}

/** Announces one "Loading" status for the placeholder shapes inside it. */
export function SkeletonGroup ({
  label = 'Loading',
  className,
  children
}: { label?: string; className?: string; children: ReactNode }) {
  return (
    <div className={cx('skeleton-group', className)} role="status" aria-busy="true">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  )
}

/** Card placeholders: a title line and `lines` text lines each, stacked or in a grid. */
export function SkeletonCards ({
  count = 3,
  lines = 2,
  grid = false,
  label,
  className
}: { count?: number; lines?: number; grid?: boolean; label?: string; className?: string }) {
  return (
    <SkeletonGroup label={label} className={cx('skeleton-cards', grid && 'skeleton-cards--grid', className)}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton-card">
          <Skeleton width="55%" height="1.1em" />
          {Array.from({ length: lines }, (_, j) => (
            <Skeleton key={j} width={j === lines - 1 ? '70%' : '100%'} />
          ))}
        </div>
      ))}
    </SkeletonGroup>
  )
}

/* ---------------------------------------------------------------------------
   Modal (ion-modal with is-open)
   ------------------------------------------------------------------------ */

export interface ModalProps {
  isOpen: boolean
  /** Fired when the user dismisses (backdrop, Escape). The parent owns `isOpen`. */
  onDidDismiss?: () => void
  breakpoints?: number[]
  initialBreakpoint?: number
  backdropDismiss?: boolean
  fullscreen?: boolean
  className?: string
  children?: ReactNode
  /**
   * Accessible name. The dialog is otherwise labelled by its first visible
   * heading (a PageHeader title or h1–h3); this is the fallback when there is
   * none, e.g. while the content is still loading.
   */
  ariaLabel?: string
  /** Explicit id of the element that names the dialog; skips heading detection. */
  ariaLabelledby?: string
  /**
   * Portal to <body> (default). `false` renders the dialog in place — it is
   * still in the top layer — so page-scoped CSS keeps reaching its content.
   */
  portal?: boolean
}

/** Native <dialog> modal; mounted only while open so showModal/close track `isOpen`. */
export function Modal (props: ModalProps) {
  if (!props.isOpen) return null
  if (props.portal === false) return <ModalDialog {...props} />
  return createPortal(<ModalDialog {...props} />, document.body)
}

const MODAL_HEADING = '.toolbar-title, h1, h2, h3'

function ModalDialog ({
  onDidDismiss,
  breakpoints,
  initialBreakpoint,
  backdropDismiss = true,
  fullscreen,
  className,
  children,
  ariaLabel,
  ariaLabelledby
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const cancel = () => {
    if (backdropDismiss) onDidDismiss?.()
  }
  useModalDialog(dialogRef, { onCancel: cancel, initialFocus: () => panelRef.current })

  // Point aria-labelledby at the visible heading (re-checked on every render
  // because modal content often swaps from a spinner to the real header).
  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || ariaLabelledby) return
    const heading = panelRef.current?.querySelector<HTMLElement>(MODAL_HEADING)
    if (heading) {
      if (!heading.id) heading.id = `${titleId}-title`
      dialog.setAttribute('aria-labelledby', heading.id)
    } else {
      dialog.removeAttribute('aria-labelledby')
    }
  })

  const sheet = Boolean(breakpoints?.length) && !fullscreen
  const sheetHeight = sheet && initialBreakpoint ? `${Math.round(initialBreakpoint * 100)}dvh` : undefined
  return (
    <dialog
      ref={dialogRef}
      className={cx('overlay-backdrop', sheet && 'overlay-backdrop--sheet')}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      onMouseDown={(e) => {
        // The dialog element spans the viewport; a press on it (not the panel) is a backdrop press.
        if (e.target === e.currentTarget) cancel()
      }}
    >
      <div
        ref={panelRef}
        className={cx('modal', sheet && 'modal--sheet', fullscreen && 'modal--fullscreen', className)}
        style={sheetHeight ? { height: sheetHeight, maxHeight: sheetHeight } : undefined}
        tabIndex={-1}
      >
        {children}
      </div>
    </dialog>
  )
}

/* ---------------------------------------------------------------------------
   Pull to refresh (ion-refresher)
   ------------------------------------------------------------------------ */

/**
 * Pull-to-refresh for touch devices. Place as the first child of <Content>.
 * `onRefresh` may return a promise; the indicator stays until it settles.
 */
export function Refresher ({ onRefresh, disabled }: { onRefresh: () => unknown; disabled?: boolean; slot?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [pull, setPull] = useState(0)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const scroller = ref.current?.parentElement
    if (!scroller || disabled) return
    // Routed pages scroll the document; <Content> only scrolls itself inside
    // modals and the menu. Read whichever is actually scrolling.
    const scrollTop = () =>
      /(auto|scroll)/.test(getComputedStyle(scroller).overflowY) ? scroller.scrollTop : window.scrollY
    let startY: number | null = null
    let distance = 0
    const onStart = (e: TouchEvent) => {
      startY = scrollTop() <= 0 ? e.touches[0].clientY : null
      distance = 0
    }
    const onMove = (e: TouchEvent) => {
      if (startY === null) return
      distance = Math.max(0, Math.min(96, (e.touches[0].clientY - startY) * 0.5))
      setPull(distance)
    }
    const onEnd = async () => {
      if (startY === null) return
      startY = null
      if (distance >= 64) {
        setBusy(true)
        setPull(48)
        try { await onRefresh() } finally {
          setBusy(false)
          setPull(0)
        }
      } else {
        setPull(0)
      }
    }
    scroller.addEventListener('touchstart', onStart, { passive: true })
    scroller.addEventListener('touchmove', onMove, { passive: true })
    scroller.addEventListener('touchend', onEnd)
    return () => {
      scroller.removeEventListener('touchstart', onStart)
      scroller.removeEventListener('touchmove', onMove)
      scroller.removeEventListener('touchend', onEnd)
    }
  }, [onRefresh, disabled])

  return (
    <div ref={ref} className="refresher" style={{ height: pull }} aria-hidden={!busy}>
      {busy || pull > 0 ? <span className="spinner spinner--small" style={{ marginBottom: 12, opacity: busy ? 1 : pull / 64 }} /> : null}
    </div>
  )
}

